import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usersAPI, centersAPI } from '../utils/api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import AccessibleModal from '../components/common/AccessibleModal';
import { CANADIAN_PROVINCES_AND_TERRITORIES } from '../constants/canada';

const ROLES = ['admin', 'manager', 'staff'];

// Liste des fonctions
const JOB_FUNCTIONS = {
  staff: [
    'Personnel polyvalent',
    'Médecin',
    'Infirmier',
    'PAB',
    'Travailleur social',
    'Ergothérapeute',
    'Physiothérapeute',
    'Technicien(ne) en réadaptation',
    'Intervenant(e) en loisirs',
    'Animateur(trice) d\'activités physiques',
    'Cuisinier(ère)',
    'Préposé(e) aux repas',
    'Chauffeur',
    'Accompagnateur(trice) transport',
    'Agent administratif',
    'Réceptionniste',
    'Agent d\'entretien',
    'Autre',
  ],
  admin: ['Administrateur', 'Directeur général', 'Responsable conformité'],
  manager: ['Manager', 'Coordonnateur(trice) clinique', 'Chef d\'équipe'],
};

const StaffModal = ({ user: editUser, centers, onClose, onSave, t, readOnly = false }) => {
  const [form, setForm] = useState({
    firstName: editUser?.first_name || '',
    lastName: editUser?.last_name || '',
    email: editUser?.email || '',
    role: editUser?.role || 'staff',
    centerId: editUser?.center_id || '',
    centerIds: editUser?.center_ids || (editUser?.center_id ? [editUser.center_id] : []),
    phone: editUser?.phone || '',
    languagePref: editUser?.language_pref || 'fr',
    jobFunction: editUser?.job_function || '',
    streetNumber: editUser?.street_number || '',
    streetName: editUser?.street_name || '',
    apartment: editUser?.apartment || '',
    city: editUser?.city || '',
    province: editUser?.province || '',
    postalCode: editUser?.postal_code || '',
    timeClockPin: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setError('');
    setLoading(true);
    try {
      const payload = {
        ...form,
        centerId: form.centerId || null,
        centerIds: form.centerIds || [],
        timeClockPin: form.timeClockPin || undefined,
      };

      let response;
      if (editUser?.id) response = await usersAPI.update(editUser.id, payload);
      else response = await usersAPI.create(payload);
      onSave(response?.data || null);
    } catch (err) {
      const firstValidationError = err.response?.data?.errors?.[0]?.msg;
      setError(firstValidationError || err.response?.data?.message || t('common.error'));
    } finally { setLoading(false); }
  };

  const availableFunctions = JOB_FUNCTIONS[form.role] || [];

  return (
    <AccessibleModal
      title={readOnly ? 'Consulter employé' : editUser ? t('staff.editStaff') : t('staff.addStaff')}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {readOnly ? t('common.close') : t('common.cancel')}
          </button>
          {!readOnly && (
            <button type="submit" form="staff-form" className="btn btn-primary" disabled={loading}>{loading ? t('common.loading') : t('common.save')}</button>
          )}
        </>
      )}
    >
        <form id="staff-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-error" role="alert">⚠ {error}</div>}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('staff.firstName')} *</label>
                <input className="form-input" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required disabled={readOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('staff.lastName')} *</label>
                <input className="form-input" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required disabled={readOnly} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('staff.email')} *</label>
              <input type="email" className="form-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required disabled={readOnly || !!editUser} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('staff.role')} *</label>
                <select className="form-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value, jobFunction: '' }))} disabled={readOnly}>
                  {ROLES.map(r => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
                </select>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                  Le role controle les permissions dans l'application.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Fonction *</label>
                <select 
                  className="form-select" 
                  value={form.jobFunction} 
                  onChange={e => setForm(f => ({ ...f, jobFunction: e.target.value }))}
                  required
                  disabled={readOnly}
                >
                  <option value="">— Sélectionner —</option>
                  {availableFunctions.map(fn => <option key={fn} value={fn}>{fn}</option>)}
                </select>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                  La fonction représente le métier (ex: Médecin, Infirmier, PAB).
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('staff.center')}</label>
                <select className="form-select" value={form.centerId} onChange={e => setForm(f => ({ ...f, centerId: e.target.value }))} disabled={readOnly}>
                  <option value="">{t('common.all')}</option>
                  {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                  Centre principal.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('staff.phone')}</label>
                <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} disabled={readOnly} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">No. rue</label>
                <input
                  className="form-input"
                  value={form.streetNumber}
                  onChange={e => setForm(f => ({ ...f, streetNumber: e.target.value }))}
                  disabled={readOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Nom de la rue</label>
                <input
                  className="form-input"
                  value={form.streetName}
                  onChange={e => setForm(f => ({ ...f, streetName: e.target.value }))}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Appartement</label>
                <input
                  className="form-input"
                  value={form.apartment}
                  onChange={e => setForm(f => ({ ...f, apartment: e.target.value }))}
                  disabled={readOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Ville</label>
                <input
                  className="form-input"
                  value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Province</label>
                <select
                  className="form-select"
                  value={form.province}
                  onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
                  disabled={readOnly}
                >
                  <option value="">— Sélectionner —</option>
                  {CANADIAN_PROVINCES_AND_TERRITORIES.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Code postal</label>
                <input
                  className="form-input"
                  value={form.postalCode}
                  onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Centres assignés (multi-sélection)</label>
              <select
                className="form-select"
                multiple
                value={form.centerIds}
                onChange={e => {
                  const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  setForm(f => ({ ...f, centerIds: selected }));
                }}
                disabled={readOnly}
                style={{ minHeight: 120 }}
              >
                {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                Ctrl/Cmd + clic pour choisir plusieurs centres.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Langue</label>
              <select className="form-select" value={form.languagePref} onChange={e => setForm(f => ({ ...f, languagePref: e.target.value }))} disabled={readOnly}>
                <option value="fr">Français</option>
                <option value="en">Anglais</option>
              </select>
            </div>
            {!readOnly && (
              <div className="form-group">
                <label className="form-label">PIN pointage tablette</label>
                <input
                  type="password"
                  inputMode="numeric"
                  className="form-input"
                  value={form.timeClockPin}
                  onChange={e => setForm(f => ({ ...f, timeClockPin: e.target.value.replace(/\D/g, '').slice(0, 12) }))}
                  placeholder={editUser?.has_timeclock_pin ? 'Laisser vide pour conserver le PIN actuel' : 'Ex. 1234'}
                />
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                  PIN numerique utilise pour le kiosque tablette. Minimum recommande : 4 chiffres.
                </div>
              </div>
            )}
            {!editUser && !readOnly && (
              <div className="alert alert-warning" role="status">📧 Un mot de passe temporaire sera envoyé par courriel.</div>
            )}
        </form>
    </AccessibleModal>
  );
};

const StaffPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modalUser, setModalUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [resettingUserId, setResettingUserId] = useState(null);
  const [regeneratingPinUserId, setRegeneratingPinUserId] = useState(null);
  const [generatedPinNotice, setGeneratedPinNotice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, centersRes] = await Promise.all([
        usersAPI.getAll({ search, role: roleFilter }),
        centersAPI.getAll(),
      ]);
      const staffOnly = (usersRes.data.users || []).filter((u) => u.role !== 'usager');
      setUsers(staffOnly);
      setCenters(centersRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, roleFilter]);
  useEffect(() => {
    const closeMenu = () => setOpenMenuUserId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    await usersAPI.delete(id);
    load();
  };

  const handleResetPassword = async (targetUser) => {
    const confirmMsg = `Envoyer un nouveau mot de passe temporaire à ${targetUser.first_name} ${targetUser.last_name} (${targetUser.email}) ?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setResettingUserId(targetUser.id);
      await usersAPI.resetPassword(targetUser.id);
      alert('Nouveau mot de passe temporaire envoyé par courriel.');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du renvoi du mot de passe');
    } finally {
      setResettingUserId(null);
    }
  };

  const handleRegeneratePin = async (targetUser) => {
    const confirmMsg = `Regenerer le PIN tablette de ${targetUser.first_name} ${targetUser.last_name} et lui envoyer par courriel ?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setRegeneratingPinUserId(targetUser.id);
      const res = await usersAPI.regenerateTimeClockPin(targetUser.id);
      setGeneratedPinNotice({
        pin: res.data?.generatedTimeClockPin,
        name: `${targetUser.first_name} ${targetUser.last_name}`,
        autoGenerated: true,
      });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la regeneration du PIN');
    } finally {
      setRegeneratingPinUserId(null);
    }
  };

  const roleBadge = (role) => {
    const map = { admin: 'badge-danger', manager: 'badge-warning', staff: 'badge-primary', usager: 'badge-success' };
    return <span className={`badge ${map[role] || 'badge-gray'}`}>{t(`roles.${role}`)}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('staff.title')}</h2>
          <p className="text-muted">{users.length} {t('common.all').toLowerCase()}</p>
        </div>
        {user?.role === 'admin' && (
          <button className="btn btn-primary" onClick={() => { setModalUser(null); setModalReadOnly(false); setShowModal(true); }}>
            + {t('staff.addStaff')}
          </button>
        )}
      </div>

      {generatedPinNotice ? (
        <div className="alert alert-success" role="status" style={{ marginBottom: 16 }}>
          {generatedPinNotice.autoGenerated ? 'PIN tablette genere' : 'PIN tablette enregistre'} pour {generatedPinNotice.name} : <strong style={{ letterSpacing: '0.08em' }}>{generatedPinNotice.pin}</strong>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="search-input-wrapper">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input className="form-input search-input" placeholder={t('staff.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 160 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">{t('common.all')}</option>
          {ROLES.map(r => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('staff.employeeNumber')}</th>
                  <th>{t('staff.firstName')} / {t('staff.lastName')}</th>
                  <th>{t('staff.email')}</th>
                  <th>{t('staff.role')}</th>
                  <th>Fonction</th>
                  <th>{t('staff.center')}</th>
                  <th>PIN tablette</th>
                  <th>{t('staff.lastLogin')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{t('staff.noStaff')}</td></tr>
                )}
                {users.map(u => (
                  <tr key={u.id}>
                    <td><code style={{ fontSize: 12, background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{u.employee_number}</code></td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{u.phone}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{u.email}</td>
                    <td>{roleBadge(u.role)}</td>
                    <td style={{ fontSize: 13 }}>{u.job_function || '—'}</td>
                    <td style={{ fontSize: 13 }}>
                      {Array.isArray(u.center_names) && u.center_names.length
                        ? u.center_names.join(', ')
                        : (u.center_name || '—')}
                    </td>
                    <td>{u.has_timeclock_pin ? <span className="badge badge-success">Configure</span> : <span className="badge badge-gray">Absent</span>}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                      {u.last_login ? format(new Date(u.last_login), 'dd/MM/yy HH:mm') : '—'}
                    </td>
                    <td>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuUserId(curr => (curr === u.id ? null : u.id));
                          }}
                          aria-label="Ouvrir le menu d'actions"
                          aria-haspopup="menu"
                          aria-expanded={openMenuUserId === u.id}
                        >
                          ⋮
                        </button>
                        {openMenuUserId === u.id && (
                          <div
                            role="menu"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'absolute',
                              top: '110%',
                              right: 0,
                              minWidth: 160,
                              background: 'white',
                              border: '1px solid var(--gray-200)',
                              borderRadius: 8,
                              boxShadow: 'var(--shadow-md)',
                              zIndex: 20,
                              padding: 6,
                            }}
                          >
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                              onClick={() => {
                                setModalUser(u);
                                setModalReadOnly(true);
                                setShowModal(true);
                                setOpenMenuUserId(null);
                              }}
                            >
                              Consulter
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                              onClick={() => {
                                setModalUser(u);
                                setModalReadOnly(false);
                                setShowModal(true);
                                setOpenMenuUserId(null);
                              }}
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                              onClick={() => {
                                setOpenMenuUserId(null);
                                handleRegeneratePin(u);
                              }}
                              disabled={regeneratingPinUserId === u.id || user?.role !== 'admin'}
                              title={user?.role !== 'admin' ? 'Action reservee a un administrateur' : ''}
                            >
                              {regeneratingPinUserId === u.id ? 'Generation...' : 'Regenerer le PIN'}
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                              onClick={() => {
                                setOpenMenuUserId(null);
                                handleResetPassword(u);
                              }}
                              disabled={resettingUserId === u.id || user?.role !== 'admin'}
                              title={user?.role !== 'admin' ? 'Action reservee a un administrateur' : ''}
                            >
                              {resettingUserId === u.id ? 'Envoi...' : 'Renvoyer le mot de passe'}
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              style={{ width: '100%', justifyContent: 'flex-start' }}
                              onClick={() => {
                                setOpenMenuUserId(null);
                                handleDelete(u.id);
                              }}
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <StaffModal
          user={modalUser}
          centers={centers}
          onClose={() => setShowModal(false)}
          onSave={(result) => {
            setShowModal(false);
            if (result?.generatedTimeClockPin && result?.user) {
              setGeneratedPinNotice({
                pin: result.generatedTimeClockPin,
                name: `${result.user.first_name} ${result.user.last_name}`,
                autoGenerated: !!result.autoGeneratedTimeClockPin,
              });
            } else {
              setGeneratedPinNotice(null);
            }
            load();
          }}
          t={t}
          readOnly={modalReadOnly}
        />
      )}
    </div>
  );
};

export default StaffPage;
